import React, { useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { useGetMiniProfileFromEmail } from 'Deer/hooks/dataHooks/useGetMiniProfileFromEmail';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import AddFeedbackButton from 'screens/Levelup/LevelUpCF/components/addFeedbackButton/AddFeedbackButton';
import { useSelectedFeedbackViewFromParam } from 'screens/Levelup/hooks/useSelectedFeedbackViewFromParam';
import CreateCheckInSidepanel from 'screens/admin/CheckIn/CreateCheckInSidepanel';
import { QUICK_ACTIONS_LIST } from 'screens/home/components/QuickActions/constants';
import { useGetEmployeeViewerRelationshipData } from 'screens/home/components/QuickActions/dataHooks';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { checkShowExternalEmployeeId } from 'utils/helpers/featureFlags';

import { Email1, LocationIcon, ManagerThickOutlined, RoleIcon } from 'assets/assetsV1/iconComponents';
import { Department } from 'assets/assetsV1/iconComponents/Department';

import { FEEDBACK_FORM_TYPE } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { pushFeedbackParams } from 'constants/helper';
import { EVENT_SOURCES } from 'constants/trackingEvents';

import { Avatar } from 'uiToolkitV1/Avatar';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextHeader2, TextLabel } from 'uiToolkitV1/Typography';

import { EmployeeId } from '../../../assets/assetsV1/iconComponents/EmployeeId';

const ProfileInfoRow = ({ Icon, label = '', subText = '', renderSeparator, isSmallSize }) => (
  <Flex alignItems="center">
    <Flex alignItems="center" px={2} py={isSmallSize ? 0 : 1} gap={2}>
      <Icon fill="transparent" color="icon.g2" boxSize="24px" />
      <TextBodyMedium color="text.g1">
        {subText} {label}
      </TextBodyMedium>
    </Flex>

    {renderSeparator && (
      <TextHeader2 bold px={1} color="text.g3">
        •
      </TextHeader2>
    )}
  </Flex>
);

const ProfileInfo = ({ employee }) => {
  const { data: managerMiniProfile } = useGetMiniProfileFromEmail({ email: employee?.manager?.email });
  const { displayName: managerName } = managerMiniProfile || {};

  const secondRowData = [];
  if (employee?.email) {
    secondRowData.push({ Icon: Email1, label: employee?.email });
  }
  if (employee?.externalEmployeeId && checkShowExternalEmployeeId()) {
    secondRowData.push({ Icon: EmployeeId, label: employee?.externalEmployeeId, subText: 'Employee ID:' });
  }

  const thirdRowData = [];

  if (employee?.designation) {
    thirdRowData.push({ Icon: RoleIcon, label: employee?.designation });
  }
  if (employee?.employeeFunction) {
    thirdRowData.push({ Icon: Department, label: employee?.employeeFunction });
  }
  if (managerName) {
    thirdRowData.push({ Icon: ManagerThickOutlined, label: managerName });
  }
  if (employee?.location) {
    thirdRowData.push({ Icon: LocationIcon, label: employee?.location });
  }

  return (
    <Flex
      alignItems="center"
      py={3}
      px={4}
      gap={3}
      borderTopRadius="8px"
      background="linear-gradient(91deg, #F0FBFF -17.68%, rgba(188, 234, 245, 0.10) 116.34%)"
    >
      <Avatar src={employee?.profilePhotoSrc} name={employee?.displayName} size={84} />
      <Flex flexDir="column" ml="12px" flexGrow={1}>
        <Flex flexDir="column" px={3} pt={2} pb={1}>
          <TextHeader2 bold>{employee?.displayName}</TextHeader2>
        </Flex>

        <Flex width="100%">
          {secondRowData.map((props, index) => (
            <ProfileInfoRow
              {...props}
              key={props.label}
              ml={index === 0 ? '0px' : '16px'}
              renderSeparator={index < secondRowData.length - 1}
              isSmallSize
            />
          ))}
        </Flex>

        <Flex width="100%">
          {thirdRowData.map((props, index) => (
            <ProfileInfoRow
              {...props}
              key={props.label}
              ml={index === 0 ? '0px' : '16px'}
              renderSeparator={index < thirdRowData.length - 1}
            />
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

const ProfileQuickActions = ({ employee }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isOneOnOnePanelOpen, setIsOneOnOnePanelOpen] = useState(false);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const { selectedFeedbackView } = useSelectedFeedbackViewFromParam();

  const handleRequestNewFeedbackClick = () => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.REQUESTED,
      newForm: true,
      empId: employee?.id ?? null,
      source: EVENT_SOURCES.FEEDBACK_PAGE.MY_FEEDBACK,
      selectedFeedbackView,
    });
  };
  const handleShareNewFeedbackClick = (isSharePublicPraise = false) => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.PROVIDED,
      newForm: true,
      empId: null,
      source: EVENT_SOURCES.FEEDBACK_PAGE.MY_FEEDBACK,
      isSharePublicPraise,
      selectedFeedbackView,
    });
  };

  const { data: isLoggedInPersonManagerOfManagerOfProfilePerson, isLoading } = useGetEmployeeViewerRelationshipData({
    loggedInEmployeeId: loggedInEmployee?.id,
    employeeId: employee?.id,
  });

  const isProfileEmployeeDirectReportee = employee?.manager?.email === loggedInEmployee?.email;

  /* Access Control code starts here */
  const { canCreateKeyResultUnderKeyResult, canCreateKeyResultUnderObjective, canCreateKeyResultAtRootLevel } =
    useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const canCreateKeyResult =
    canCreateKeyResultAtRootLevel || canCreateKeyResultUnderObjective || canCreateKeyResultUnderKeyResult;

  const { keyResultsName } = useOrganisationSettings();

  const FILTERED_QUICK_ACTIONS_LIST = QUICK_ACTIONS_LIST.filter(({ displayName }) => {
    if (displayName === 'Create a Goal') {
      return canCreateKeyResult;
    }
    if (
      displayName === 'Request feedback' ||
      displayName === 'Share feedback' ||
      displayName === 'Share public praise'
    ) {
      return false;
    }
    return true;
  });
  /* Access Control code ends here */

  if (isLoading) return null;

  return (
    <Flex background="white" alignItems="flex-start" borderBottomRadius="8px" borderTop="1px solid #EBEFF3">
      <TextLabel p={3} mr="4" bold color="text.g2" whiteSpace="nowrap">
        Quick Actions:
      </TextLabel>

      <Flex flexWrap="wrap" flexGrow={1}>
        {FILTERED_QUICK_ACTIONS_LIST.slice(0, -1).map(
          ({ displayName, Icon, isActionVisible, onClick, hideOnProfilePage = false, Comp }) => {
            if (isActionVisible()) {
              if (typeof hideOnProfilePage === 'function') {
                if (
                  hideOnProfilePage({
                    empId: employee?.id,
                    isLoggedInPersonManagerOfManagerOfProfilePerson,
                    isProfileEmployeeDirectReportee,
                  })
                )
                  return null;
              }
              if (Comp) return <Comp key={displayName} employeeId={employee?.id} withPadding />;

              let displayNameModified = displayName;
              if (displayName === 'Create a Goal') {
                displayNameModified = `Create ${modifyString(
                  keyResultsName,
                  StringModifier.LOWERCASE_ARTICLE
                )} ${keyResultsName}`;
              }
              return (
                <Flex
                  key={displayName}
                  alignItems="center"
                  cursor="pointer"
                  gap={2}
                  px={3}
                  py={2}
                  minW="160px"
                  onClick={() =>
                    onClick({
                      employee,
                      dispatch,
                      setIsOneOnOnePanelOpen,
                      history,
                      // employeeId: employee?.id,
                      source: EVENT_SOURCES.PROFILE_PAGE,
                      location,
                    })
                  }
                >
                  <Icon fill="transparent" boxSize="24px" color="text.g2" />
                  <TextBodyMedium color="text.g1">{displayNameModified}</TextBodyMedium>
                </Flex>
              );
            }
            return null;
          }
        )}
        {loggedInEmployee?.id !== employee?.id && (
          <AddFeedbackButton
            onClickShareFeedback={(isSharePublicPraise) => handleShareNewFeedbackClick(isSharePublicPraise)}
            onClickRequestFeedback={handleRequestNewFeedbackClick}
            isQuickAction
          />
        )}
      </Flex>
      <CreateCheckInSidepanel
        isOpen={isOneOnOnePanelOpen}
        setIsOpen={setIsOneOnOnePanelOpen}
        currentOneOnOneEmployees={[]}
        loggedInEmployeeId={employee?.id}
      />
    </Flex>
  );
};

const ProfileHeaderCard = ({ employee }) => (
  <PaperWithShadow p={0}>
    <Flex flexDir="column" w="100%" borderRadius="8px">
      <ProfileInfo employee={employee} />
      <ProfileQuickActions employee={employee} />
    </Flex>
  </PaperWithShadow>
);

export { ProfileHeaderCard };
